import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Star, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { errorMessage } from '@/lib/guest';
import { submitFeedback } from '@/lib/mutations';
import { Button } from '@/components/ui/button';

/**
 * The guest rates the visit once the food has arrived. A rating
 * of 2 or less opens a service-recovery case on the venue's side; the guest is
 * told nothing different and just gets a thank-you.
 *
 * It asks from behind a button rather than from a card on the page: five stars,
 * a comment box and a submit button is a lot of vertical space spent on a
 * question the guest has not been asked yet. The button costs one row; the form
 * appears when they choose to answer.
 *
 * The form takes no order: `FeedbackController` deletes `orderId`, `customerId`
 * and `checkId` from a guest's body and binds `sessionId` and `branchId` from
 * the token, so a guest cannot file feedback against another table's order and
 * naming one here would only be theatre. The visit is the token's visit.
 */
export function FeedbackCard() {
  const { t } = useTranslation();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [open, setOpen] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);

  /**
   * `showModal()` rather than an `open` attribute or a div: the native dialog
   * traps focus, closes on Escape, renders in the top layer above any stacking
   * context, and returns focus to the button that opened it. Rebuilding that on
   * a div is how modals end up unreachable by keyboard.
   */
  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  const submit = async () => {
    setSending(true);
    try {
      // The rating is on disk before the network is touched, so one taken
      // as the guest walks out is not lost with the connection.
      const { synced } = await submitFeedback({
        rating,
        ...(comment.trim() ? { comment: comment.trim() } : {}),
      });
      if (!synced) toast.success(t('guest.feedback.queued'));
      setSent(true);
      setOpen(false);
    } catch (error) {
      toast.error(errorMessage(error, t('guest.feedback.failed')));
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <p className="py-2 text-center text-xs text-muted-foreground">{t('guest.feedback.thanks')}</p>
    );
  }

  return (
    <>
      <div className="flex justify-center py-2">
        <button
          type="button"
          onClick={() => setOpen(true)}
          // The label is on the button rather than beside it: a bare star is
          // ambiguous (favourite? bookmark?), and a screen reader would
          // otherwise announce nothing at all.
          aria-label={t('guest.feedback.title')}
          title={t('guest.feedback.title')}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-line-strong text-muted-foreground transition-colors hover:text-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          <Star className="h-5 w-5" />
        </button>
      </div>

      <dialog
        ref={dialogRef}
        onClose={() => setOpen(false)}
        // A click on the backdrop lands on the dialog element itself, never on
        // its contents, which is what distinguishes the two here.
        onClick={(e) => {
          if (e.target === dialogRef.current) setOpen(false);
        }}
        aria-labelledby="feedback-title"
        className="w-[calc(100vw-2rem)] max-w-sm rounded-xl border border-line-strong bg-background p-0 text-foreground backdrop:bg-black/50"
      >
        <div className="space-y-3 p-5">
          <div className="flex items-start justify-between gap-2">
            <h2 id="feedback-title" className="text-sm font-medium">
              {t('guest.feedback.title')}
            </h2>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={t('common.close')}
              className="-me-1 -mt-1 rounded-lg p-1.5 text-muted-foreground hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div
            className="flex justify-center gap-1"
            role="radiogroup"
            aria-label={t('guest.feedback.rating')}
          >
            {[1, 2, 3, 4, 5].map((value) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={rating === value}
                aria-label={String(value)}
                onClick={() => setRating(value)}
                className="p-1.5 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring"
              >
                <Star
                  className={cn(
                    'h-8 w-8',
                    value <= rating ? 'fill-accent text-accent' : 'text-line-strong',
                  )}
                />
              </button>
            ))}
          </div>

          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            placeholder={t('guest.feedback.comment')}
            className="w-full min-w-0 rounded-lg border border-line-strong p-3 text-sm focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring"
          />

          <Button className="w-full" disabled={!rating || sending} onClick={() => void submit()}>
            {sending ? t('common.loading') : t('guest.feedback.submit')}
          </Button>
        </div>
      </dialog>
    </>
  );
}
